import React, { useState, useEffect } from "react";
import {
  Select,
  Button,
  Table,
  Radio,
  notification,
  Tag,
  Divider,
  Row,
  Col,
  Modal,
  Alert,
  DatePicker,
  Popconfirm,
} from "antd";
import { CompressOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getStudentsApi,
  mergeStudentsApi,
} from "../../../../api/student";
import { getAccessToken } from "../../../../api/auth";
import { getFinalConditionsActiveApi } from "../../../../api/finalCondition";
import { getAdvisersActiveApi } from "../../../../api/adviser";

const { Option } = Select;
const { confirm } = Modal;

// Campos escalares a comparar campo por campo
const FIELDS = [
  { key: "lastname", label: "Apellido" },
  { key: "name", label: "Nombre" },
  { key: "documentNumber", label: "Nro. Documento" },
  {
    key: "docType",
    label: "Tipo Doc.",
    display: function(s) { return s.docType ? s.docType.description : ""; },
  },
  { key: "email", label: "Email" },
  { key: "phone", label: "Teléfono" },
  { key: "mobile", label: "Móvil" },
  { key: "country", label: "País" },
  { key: "province", label: "Provincia" },
  { key: "city", label: "Ciudad" },
  { key: "postalCode", label: "Cod. Postal" },
  {
    key: "birthDate",
    label: "Fecha Nacimiento",
    display: function(s) {
      return s.birthDate ? new Date(s.birthDate).toLocaleDateString("es-AR") : "";
    },
  },
  {
    key: "entryDate",
    label: "Fecha de Ingreso",
    display: function(s) {
      return s.entryDate ? new Date(s.entryDate).toLocaleDateString("es-AR") : "";
    },
  },
  {
    key: "specialty",
    label: "Especialidad",
    display: function(s) { return s.specialty ? s.specialty.description : ""; },
  },
  {
    key: "entryCondition",
    label: "Condición de Ingreso",
    display: function(s) { return s.entryCondition ? s.entryCondition.description : ""; },
  },
  { key: "yearAdmission", label: "Año de Ingreso" },
  { key: "yearGraduation", label: "Año de Egreso" },
  { key: "hoursCoursed", label: "Horas Cursadas" },
  { key: "scholarshipGrantor", label: "Becado por" },
  { key: "maritalStatus", label: "Estado Civil" },
  {
    key: "foreign",
    label: "Extranjero",
    display: function(s) { return s.foreign ? "Sí" : "No"; },
  },
  {
    key: "active",
    label: "Activo",
    display: function(s) { return s.active ? "Sí" : "No"; },
  },
  { key: "observation", label: "Observación" },
];

function getDisplayValue(student, field) {
  if (!student) return "";
  var val = field.display ? field.display(student) : student[field.key];
  if (val === null || val === undefined) return "";
  return String(val);
}

// Para campos de referencia (ObjectId), extraer el _id; para el resto, el valor directo
function getRawValue(student, key) {
  var val = student[key];
  if (val && typeof val === "object" && val._id) return val._id;
  return val;
}

// Normalizar un item de array: reemplazar objetos poblados por su _id
function normalizeId(val) {
  if (val && typeof val === "object" && val._id) return val._id;
  return val;
}

function safeToString(val) {
  var norm = normalizeId(val);
  return norm ? norm.toString() : null;
}

// Une los arrays finalConditions (o advisers) de ambos alumnos, ordena por initDate
// y deja abierto ÚNICAMENTE el último registro del alumno preferido (keepSrc: 'A'|'B').
// Los demás registros abiertos se cierran usando el initDate del siguiente como finalDate.
function mergeAndCloseEntries(arrA, arrB, keepSrc, mapFn) {
  var tagged = []
    .concat((arrA || []).map(function(item) { return Object.assign({}, mapFn(item), { _src: "A" }); }))
    .concat((arrB || []).map(function(item) { return Object.assign({}, mapFn(item), { _src: "B" }); }));

  tagged.sort(function(a, b) {
    if (!a.initDate && !b.initDate) return 0;
    if (!a.initDate) return 1;
    if (!b.initDate) return -1;
    return new Date(a.initDate) - new Date(b.initDate);
  });

  var keepOpenIdx = -1;
  tagged.forEach(function(item, idx) {
    if (!item.finalDate && item._src === keepSrc) keepOpenIdx = idx;
  });
  if (keepOpenIdx === -1) {
    tagged.forEach(function(item, idx) {
      if (!item.finalDate) keepOpenIdx = idx;
    });
  }

  tagged.forEach(function(item, idx) {
    if (!item.finalDate && idx !== keepOpenIdx) {
      var closingDate = null;
      for (var i = idx + 1; i < tagged.length; i++) {
        if (tagged[i].initDate) { closingDate = tagged[i].initDate; break; }
      }
      tagged[idx] = Object.assign({}, item, {
        finalDate: closingDate || new Date().toISOString(),
      });
    }
  });

  return tagged.map(function(item) {
    var r = Object.assign({}, item);
    delete r._src;
    return r;
  });
}

function countOpen(arr) {
  return (arr || []).filter(function(item) { return !item.finalDate; }).length;
}

// Contador de claves únicas para filas editables
var _rowKeySeq = 0;
function nextRowKey() {
  _rowKeySeq = _rowKeySeq + 1;
  return _rowKeySeq;
}

function toKeyedRows(arr) {
  return (arr || []).map(function(item) {
    return Object.assign({}, item, { _key: nextRowKey() });
  });
}

export default function MergeStudentsForm({ setIsVisibleModal, setReloadStudents }) {
  var accessToken = getAccessToken();

  // Búsqueda de estudiantes
  var [searchA, setSearchA] = useState("");
  var [searchB, setSearchB] = useState("");
  var [optionsA, setOptionsA] = useState([]);
  var [optionsB, setOptionsB] = useState([]);

  // IDs seleccionados
  var [selectedIdA, setSelectedIdA] = useState(null);
  var [selectedIdB, setSelectedIdB] = useState(null);

  // Datos poblados de cada estudiante (se toman directamente de los resultados de búsqueda)
  var [studentA, setStudentA] = useState(null); // se eliminará
  var [studentB, setStudentB] = useState(null); // se conservará

  var [submitting, setSubmitting] = useState(false);

  // Selección campo por campo: 'A' | 'B'
  var [selections, setSelections] = useState({});

  // Advertencia si la fusión genera múltiples registros abiertos
  var [hasOpenWarning, setHasOpenWarning] = useState(false);

  // Selección conjunta para Condición Final + Consejero (semilla inicial de la tabla)
  var [fcAdviserSel, setFcAdviserSel] = useState("B");

  // Catálogos para los Selects de la tabla editable
  var [finalConditionsList, setFinalConditionsList] = useState([]);
  var [advisersList, setAdvisersList] = useState([]);

  // Filas editables del historial fusionado de finalConditions
  var [mergedFcRows, setMergedFcRows] = useState([]);

  // Cargar catálogos una sola vez al montar
  useEffect(function() {
    getFinalConditionsActiveApi(accessToken, true).then(function(res) {
      if (res && res.finalConditions) setFinalConditionsList(res.finalConditions);
    });
    getAdvisersActiveApi(accessToken, true).then(function(res) {
      if (res && res.advisers) setAdvisersList(res.advisers);
    });
  }, []);

  // Buscar estudiantes al tipear
  useEffect(function() {
    if (searchA.length >= 2) {
      getStudentsApi(accessToken, 1, 20, 1, searchA, null).then(function(response) {
        if (response && response.students && response.students.docs) {
          setOptionsA(response.students.docs);
        }
      });
    }
  }, [searchA]);

  useEffect(function() {
    if (searchB.length >= 2) {
      getStudentsApi(accessToken, 1, 20, 1, searchB, null).then(function(response) {
        if (response && response.students && response.students.docs) {
          setOptionsB(response.students.docs);
        }
      });
    }
  }, [searchB]);

  // Al seleccionar un estudiante, tomar los datos directamente de las opciones de búsqueda
  // (las opciones ya vienen con finalConditions, adviser, etc. poblados)
  function handleSelectA(val) {
    setSelectedIdA(val);
    for (var i = 0; i < optionsA.length; i++) {
      if (optionsA[i]._id === val) {
        setStudentA(optionsA[i]);
        break;
      }
    }
  }

  function handleSelectB(val) {
    setSelectedIdB(val);
    for (var i = 0; i < optionsB.length; i++) {
      if (optionsB[i]._id === val) {
        setStudentB(optionsB[i]);
        break;
      }
    }
  }

  // Recalcular la tabla de finalConditions a partir de los dos estudiantes
  function seedMergedFcRows(src, stA, stB) {
    var merged = mergeAndCloseEntries(
      stA.finalConditions,
      stB.finalConditions,
      src,
      function(fc) {
        return {
          finalCondition: normalizeId(fc.finalCondition),
          adviser: normalizeId(fc.adviser),
          initDate: fc.initDate || null,
          finalDate: fc.finalDate || null,
        };
      }
    );
    setMergedFcRows(toKeyedRows(merged));
  }

  // Inicializar selecciones cuando ambos estudiantes están disponibles
  useEffect(function() {
    if (studentA && studentB) {
      var init = {};
      FIELDS.forEach(function(f) {
        var valA = getDisplayValue(studentA, f);
        var valB = getDisplayValue(studentB, f);
        init[f.key] = !valB && valA ? "A" : "B";
      });
      setSelections(init);

      var hasFC_B = studentB.finalCondition;
      var hasFC_A = studentA.finalCondition;
      var newSel = !hasFC_B && hasFC_A ? "A" : "B";
      setFcAdviserSel(newSel);

      var fcOpenA = countOpen(studentA.finalConditions);
      var fcOpenB = countOpen(studentB.finalConditions);
      var advOpenA = countOpen(studentA.advisers);
      var advOpenB = countOpen(studentB.advisers);
      setHasOpenWarning(fcOpenA + fcOpenB > 1 || advOpenA + advOpenB > 1);

      seedMergedFcRows(newSel, studentA, studentB);
    }
  }, [studentA, studentB]);

  // Actualizar un campo de una fila del historial
  function handleFcRowUpdate(key, field, value) {
    setMergedFcRows(function(prev) {
      return prev.map(function(row) {
        if (row._key !== key) return row;
        var u = Object.assign({}, row);
        u[field] = value;
        return u;
      });
    });
  }

  // Eliminar una fila del historial
  function handleFcRowDelete(key) {
    setMergedFcRows(function(prev) {
      return prev.filter(function(row) { return row._key !== key; });
    });
  }

  // Buscar el nombre de un consejero por ID en advisersList o en los arrays del alumno
  function getAdviserLabel(id) {
    if (!id) return "";
    var idStr = String(id);
    for (var i = 0; i < advisersList.length; i++) {
      if (String(advisersList[i]._id) === idStr) {
        return advisersList[i].lastname + " " + advisersList[i].name;
      }
    }
    // Buscar en datos del estudiante si el consejero está poblado
    var allFc = []
      .concat(studentA ? (studentA.finalConditions || []) : [])
      .concat(studentB ? (studentB.finalConditions || []) : []);
    for (var j = 0; j < allFc.length; j++) {
      var a = allFc[j].adviser;
      if (a && typeof a === "object" && String(a._id) === idStr) {
        return (a.lastname || "") + " " + (a.name || "");
      }
    }
    return idStr.slice(-6); // últimos 6 chars del ID como fallback
  }

  // Buscar el nombre de una condición final por ID
  function getFcLabel(id) {
    if (!id) return "";
    var idStr = String(id);
    for (var i = 0; i < finalConditionsList.length; i++) {
      if (String(finalConditionsList[i]._id) === idStr) {
        return finalConditionsList[i].description;
      }
    }
    var allFc = []
      .concat(studentA ? (studentA.finalConditions || []) : [])
      .concat(studentB ? (studentB.finalConditions || []) : []);
    for (var j = 0; j < allFc.length; j++) {
      var fc = allFc[j].finalCondition;
      if (fc && typeof fc === "object" && String(fc._id) === idStr) {
        return fc.description || "";
      }
    }
    return "";
  }

  var buildMergedData = function() {
    var merged = {};

    // Campos escalares y de referencia
    FIELDS.forEach(function(f) {
      var src = selections[f.key] === "A" ? studentA : studentB;
      merged[f.key] = getRawValue(src, f.key);
    });

    // cycles: unión por schoolYear
    var mergeCycles = function() {
      var map = new Map();
      [].concat(studentA.cycles || []).concat(studentB.cycles || []).forEach(function(c) {
        var syId = safeToString(c.schoolYear);
        if (syId && !map.has(syId)) {
          map.set(syId, {
            schoolYear: normalizeId(c.schoolYear),
            studentCondition: normalizeId(c.studentCondition),
          });
        }
      });
      return Array.from(map.values());
    };

    // yearsAbandonment: unión por schoolYear
    var mergeYearsAbandonment = function() {
      var map = new Map();
      [].concat(studentA.yearsAbandonment || []).concat(studentB.yearsAbandonment || []).forEach(function(y) {
        var syId = safeToString(y.schoolYear);
        if (syId && !map.has(syId)) {
          map.set(syId, { schoolYear: normalizeId(y.schoolYear) });
        }
      });
      return Array.from(map.values());
    };

    // courses: unión por course ID (B primero para que sus datos ganen en cursos duplicados)
    var mergeCourses = function() {
      var map = new Map();
      [].concat(studentB.courses || []).concat(studentA.courses || []).forEach(function(c) {
        var cId = safeToString(c.course);
        if (cId && !map.has(cId)) {
          map.set(cId, {
            course: normalizeId(c.course),
            stateStudentCourse: normalizeId(c.stateStudentCourse),
            hoursCoursed: c.hoursCoursed,
          });
        }
      });
      return Array.from(map.values());
    };

    // finalConditions: usar las filas editadas directamente
    merged.finalConditions = mergedFcRows.map(function(row) {
      return {
        finalCondition: row.finalCondition || null,
        adviser: row.adviser || null,
        initDate: row.initDate || null,
        finalDate: row.finalDate || null,
      };
    });

    // Top-level finalCondition y adviser: derivados del último registro activo de la tabla
    var activeFcRow = null;
    mergedFcRows.forEach(function(row) {
      if (!row.finalDate) activeFcRow = row;
    });
    if (activeFcRow) {
      merged.finalCondition = activeFcRow.finalCondition || null;
      merged.adviser = activeFcRow.adviser || null;
    } else {
      var fcSrc = fcAdviserSel === "A" ? studentA : studentB;
      merged.finalCondition = getRawValue(fcSrc, "finalCondition");
      merged.adviser = getRawValue(fcSrc, "adviser");
    }

    // advisers: fusión automática
    merged.advisers = mergeAndCloseEntries(
      studentA.advisers,
      studentB.advisers,
      fcAdviserSel,
      function(a) {
        return {
          adviser: normalizeId(a.adviser),
          initDate: a.initDate,
          finalDate: a.finalDate,
        };
      }
    );

    merged.cycles = mergeCycles();
    merged.yearsAbandonment = mergeYearsAbandonment();
    merged.courses = mergeCourses();

    return merged;
  };

  var handleSubmit = function() {
    if (!studentA || !studentB) {
      notification.error({ message: "Seleccione ambos estudiantes" });
      return;
    }
    if (studentA._id === studentB._id) {
      notification.error({ message: "Debe seleccionar dos estudiantes diferentes" });
      return;
    }

    confirm({
      title: "¿Confirmar fusión?",
      content: (
        <span>
          El estudiante <strong>{studentA.lastname} {studentA.name}</strong> (A) será{" "}
          <strong>eliminado permanentemente</strong> y sus referencias migradas al
          estudiante <strong>{studentB.lastname} {studentB.name}</strong> (B).
          Esta acción no se puede deshacer.
        </span>
      ),
      okText: "Sí, fusionar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: function() {
        setSubmitting(true);
        var mergedData = buildMergedData();
        return mergeStudentsApi(accessToken, studentA._id, studentB._id, mergedData)
          .then(function(result) {
            setSubmitting(false);
            if (!result || result.code !== 200) {
              notification.error({
                message: (result && result.message) ? result.message : "Error al fusionar los estudiantes",
              });
            } else {
              notification.success({ message: result.message });
              setIsVisibleModal(false);
              setReloadStudents(true);
            }
          });
      },
    });
  };

  // Datos para la tabla comparativa
  var tableData = FIELDS.map(function(f) {
    return {
      key: f.key,
      field: f.label,
      valueA: getDisplayValue(studentA, f),
      valueB: getDisplayValue(studentB, f),
    };
  });

  var columns = [
    {
      title: "Campo",
      dataIndex: "field",
      width: "20%",
      render: function(text, record) {
        var conflict = record.valueA && record.valueB && record.valueA !== record.valueB;
        return (
          <span style={{ fontWeight: conflict ? "bold" : "normal", color: conflict ? "#fa8c16" : "inherit" }}>
            {text}
          </span>
        );
      },
    },
    {
      title: function() {
        return (
          <span>
            <Tag color="red">A</Tag> Eliminar &middot;{" "}
            <span style={{ fontWeight: "normal", fontSize: 12 }}>
              {studentA ? studentA.lastname + " " + studentA.name : "\u2014"}
            </span>
          </span>
        );
      },
      dataIndex: "valueA",
      width: "37%",
      render: function(text, record) {
        return (
          <Radio
            checked={selections[record.key] === "A"}
            onChange={function() {
              setSelections(function(prev) {
                var next = Object.assign({}, prev);
                next[record.key] = "A";
                return next;
              });
            }}
          >
            <span style={{ color: text ? "inherit" : "#bbb" }}>{text || "\u2014"}</span>
          </Radio>
        );
      },
    },
    {
      title: function() {
        return (
          <span>
            <Tag color="green">B</Tag> Conservar &middot;{" "}
            <span style={{ fontWeight: "normal", fontSize: 12 }}>
              {studentB ? studentB.lastname + " " + studentB.name : "\u2014"}
            </span>
          </span>
        );
      },
      dataIndex: "valueB",
      width: "37%",
      render: function(text, record) {
        return (
          <Radio
            checked={selections[record.key] === "B"}
            onChange={function() {
              setSelections(function(prev) {
                var next = Object.assign({}, prev);
                next[record.key] = "B";
                return next;
              });
            }}
          >
            <span style={{ color: text ? "inherit" : "#bbb" }}>{text || "\u2014"}</span>
          </Radio>
        );
      },
    },
    {
      title: "Usar",
      width: "6%",
      render: function(_, record) {
        return (
          <Tag color={selections[record.key] === "A" ? "red" : "green"}>
            {selections[record.key] || "B"}
          </Tag>
        );
      },
    },
  ];

  // Columnas de la tabla editable de historial de finalConditions
  var fcColumns = [
    {
      title: "Condición Final",
      key: "finalCondition",
      width: "24%",
      render: function(_, record) {
        var val = record.finalCondition ? String(record.finalCondition) : undefined;
        return (
          <Select
            value={val}
            onChange={function(v) { handleFcRowUpdate(record._key, "finalCondition", v || null); }}
            style={{ width: "100%" }}
            size="small"
            allowClear
            placeholder="Sin condición"
          >
            {finalConditionsList.map(function(fc) {
              return (
                <Option key={String(fc._id)} value={String(fc._id)}>
                  {fc.description}
                </Option>
              );
            })}
          </Select>
        );
      },
    },
    {
      title: "Consejero",
      key: "adviser",
      width: "26%",
      render: function(_, record) {
        var val = record.adviser ? String(record.adviser) : undefined;
        return (
          <Select
            value={val}
            onChange={function(v) { handleFcRowUpdate(record._key, "adviser", v || null); }}
            style={{ width: "100%" }}
            size="small"
            allowClear
            placeholder="Sin consejero"
            showSearch
            optionFilterProp="children"
          >
            {advisersList.map(function(adv) {
              return (
                <Option key={String(adv._id)} value={String(adv._id)}>
                  {adv.lastname} {adv.name}
                </Option>
              );
            })}
          </Select>
        );
      },
    },
    {
      title: "Fecha Inicio",
      key: "initDate",
      width: "19%",
      render: function(_, record) {
        var val = record.initDate ? moment(record.initDate) : null;
        return (
          <DatePicker
            value={val}
            onChange={function(m) {
              handleFcRowUpdate(record._key, "initDate", m ? m.toISOString() : null);
            }}
            format="DD/MM/YYYY"
            size="small"
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Fecha Fin",
      key: "finalDate",
      width: "19%",
      render: function(_, record) {
        var val = record.finalDate ? moment(record.finalDate) : null;
        if (!record.finalDate) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Tag color="green" style={{ margin: 0 }}>Activo</Tag>
              <DatePicker
                value={null}
                onChange={function(m) {
                  handleFcRowUpdate(record._key, "finalDate", m ? m.toISOString() : null);
                }}
                format="DD/MM/YYYY"
                size="small"
                style={{ flex: 1 }}
                placeholder="Cerrar..."
              />
            </div>
          );
        }
        return (
          <DatePicker
            value={val}
            onChange={function(m) {
              handleFcRowUpdate(record._key, "finalDate", m ? m.toISOString() : null);
            }}
            format="DD/MM/YYYY"
            size="small"
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "12%",
      render: function(_, record) {
        return (
          <Popconfirm
            title="¿Eliminar esta entrada del historial?"
            onConfirm={function() { handleFcRowDelete(record._key); }}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button size="small" danger type="link" icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  var bothLoaded = studentA && studentB;

  return (
    <div style={{ padding: "0 8px" }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div style={{ marginBottom: 4 }}>
            <Tag color="red">A</Tag>{" "}
            <strong>Estudiante a eliminar</strong>
          </div>
          <Select
            showSearch
            value={selectedIdA}
            placeholder="Buscar por apellido o nombre..."
            onSearch={setSearchA}
            onChange={handleSelectA}
            filterOption={false}
            style={{ width: "100%" }}
            notFoundContent={searchA.length < 2 ? "Escriba al menos 2 caracteres" : "Sin resultados"}
          >
            {optionsA.map(function(s) {
              return (
                <Option key={s._id} value={s._id}>
                  {s.lastname} {s.name} &mdash; DNI: {s.documentNumber}
                </Option>
              );
            })}
          </Select>
        </Col>
        <Col span={12}>
          <div style={{ marginBottom: 4 }}>
            <Tag color="green">B</Tag>{" "}
            <strong>Estudiante a conservar</strong>
          </div>
          <Select
            showSearch
            value={selectedIdB}
            placeholder="Buscar por apellido o nombre..."
            onSearch={setSearchB}
            onChange={handleSelectB}
            filterOption={false}
            style={{ width: "100%" }}
            notFoundContent={searchB.length < 2 ? "Escriba al menos 2 caracteres" : "Sin resultados"}
          >
            {optionsB.map(function(s) {
              return (
                <Option key={s._id} value={s._id}>
                  {s.lastname} {s.name} &mdash; DNI: {s.documentNumber}
                </Option>
              );
            })}
          </Select>
        </Col>
      </Row>

      {!bothLoaded && (
        <div style={{ textAlign: "center", color: "#888", marginTop: 32, marginBottom: 32 }}>
          Seleccione ambos estudiantes para ver la comparación campo por campo.
        </div>
      )}

      {bothLoaded && (
        <div>
          <Divider style={{ margin: "12px 0" }}>Comparación campo por campo</Divider>
          {hasOpenWarning && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 10 }}
              message="Registros abiertos en condiciones finales u orientadores"
              description={
                "Ambos estudiantes tienen registros sin fecha de cierre. " +
                "Se cerrarán automáticamente los más antiguos. Revisá y ajustá el historial " +
                "en la sección de abajo antes de confirmar la fusión."
              }
            />
          )}
          <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
            Hacé clic en el valor que querés conservar en cada campo. Los campos en{" "}
            <span style={{ color: "#fa8c16" }}>naranja</span> tienen valores distintos en ambos registros.
            Los campos de tipo lista (ciclos, orientadores, condiciones finales, años de abandono, cursos)
            se fusionan automáticamente tomando la unión de ambos estudiantes.
          </p>
          <Table
            dataSource={tableData}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ y: 380 }}
            bordered
          />

          {/* Sección editable: Historial de Condición Final */}
          <Divider style={{ margin: "16px 0 8px 0" }}>
            Historial de Condición Final y Consejero
          </Divider>
          <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
            La tabla muestra el historial fusionado de condiciones finales de ambos estudiantes, ordenado por fecha de inicio.
            La fila marcada como <Tag color="green" style={{ marginBottom: 0 }}>Activo</Tag> es el estado vigente.
            Podés editar condición, consejero, fechas, o eliminar filas antes de confirmar la fusión.
          </p>

          {/* Botones para recalcular desde alumno A o B */}
          <Row gutter={8} style={{ marginBottom: 10 }}>
            <Col>
              <span style={{ fontSize: 12, color: "#888", marginRight: 8 }}>
                Recalcular historial manteniendo activo el registro de:
              </span>
            </Col>
            <Col>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={function() {
                  setFcAdviserSel("A");
                  seedMergedFcRows("A", studentA, studentB);
                }}
                style={{
                  borderColor: fcAdviserSel === "A" ? "#ff4d4f" : undefined,
                  color: fcAdviserSel === "A" ? "#ff4d4f" : undefined,
                  marginRight: 6,
                }}
              >
                <Tag color="red" style={{ margin: "0 4px 0 0" }}>A</Tag>
                {studentA.lastname} {studentA.name}
              </Button>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={function() {
                  setFcAdviserSel("B");
                  seedMergedFcRows("B", studentA, studentB);
                }}
                style={{
                  borderColor: fcAdviserSel === "B" ? "#52c41a" : undefined,
                  color: fcAdviserSel === "B" ? "#52c41a" : undefined,
                }}
              >
                <Tag color="green" style={{ margin: "0 4px 0 0" }}>B</Tag>
                {studentB.lastname} {studentB.name}
              </Button>
            </Col>
          </Row>

          <Table
            dataSource={mergedFcRows}
            columns={fcColumns}
            rowKey="_key"
            pagination={false}
            size="small"
            bordered
            onRow={function(record) {
              return {
                style: { background: !record.finalDate ? "#f6ffed" : undefined },
              };
            }}
            locale={{ emptyText: "Sin historial de condiciones finales" }}
          />

          {/* Resumen del estado activo que quedará */}
          {mergedFcRows.length > 0 && (function() {
            var activeRow = null;
            mergedFcRows.forEach(function(row) {
              if (!row.finalDate) activeRow = row;
            });
            if (!activeRow) {
              return (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="No hay registro activo: todos los registros tienen fecha de fin."
                />
              );
            }
            var fcName = getFcLabel(activeRow.finalCondition);
            var advName = getAdviserLabel(activeRow.adviser);
            return (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, fontSize: 13 }}>
                <strong>Estado final que quedará registrado: </strong>
                <span>{fcName || "Sin condición"}</span>
                {advName ? <span> &mdash; Consejero: <strong>{advName}</strong></span> : null}
              </div>
            );
          })()}

          <Divider style={{ margin: "12px 0" }} />
          <div style={{ textAlign: "right" }}>
            <Button
              onClick={function() { setIsVisibleModal(false); }}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              danger
              icon={<CompressOutlined />}
              onClick={handleSubmit}
              loading={submitting}
            >
              Fusionar y eliminar estudiante A
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
